import React, { useRef, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormFeedback,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import * as Yup from "yup";
import { toast } from "react-toastify";
import {
  AsyncClientSelect,
  AsyncSourceSelect,
} from "components/Common/AsyncSearchSelects";
import { buildClientAsyncFilterInitialOption } from "helpers/metadataFilterHelpers";
import { uploadCandidateBulkCv } from "model/CandidateModel";

const isAllowedBulkCvFile = (file) => {
  if (!file || !(file instanceof File)) return false;
  const name = String(file.name || "").toLowerCase();
  if (name.endsWith(".pdf") || file.type === "application/pdf") return true;
  if (file.type.startsWith("image/")) return true;
  return /\.(jpe?g|png|gif|webp|bmp|heic|heif)$/i.test(name);
};

const bulkCvUploadSchema = Yup.object().shape({
  clientId: Yup.string().trim().required("Please select a client"),
  sourceId: Yup.string().trim().required("Please select a source"),
  files: Yup.array()
    .min(1, "Please choose at least one CV file")
    .test(
      "file-types",
      "Only PDF and image files are allowed",
      (files) =>
        Array.isArray(files) &&
        files.length > 0 &&
        files.every((file) => isAllowedBulkCvFile(file))
    ),
});

const formatSelectedFilesLabel = (files = []) => {
  if (!files.length) return null;
  if (files.length === 1) return files[0].name;
  return `${files.length} file(s) selected`;
};

const UploadBulkCV = () => {
  const fileInputRef = useRef(null);
  const [clientId, setClientId] = useState("");
  const [clientIdLabel, setClientIdLabel] = useState("");
  const [sourceId, setSourceId] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files ?? []);
    setSelectedFiles(files);
    if (errors.files) {
      setErrors((prev) => ({ ...prev, files: "" }));
    }
  };

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const resetUploadForm = () => {
    setClientId("");
    setClientIdLabel("");
    setSourceId("");
    setSelectedFiles([]);
    setErrors({});
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    try {
      await bulkCvUploadSchema.validate(
        {
          clientId: String(clientId ?? "").trim(),
          sourceId: String(sourceId ?? "").trim(),
          files: selectedFiles,
        },
        { abortEarly: false }
      );
      setErrors({});
    } catch (err) {
      const newErrors = {};
      err.inner.forEach((error) => {
        newErrors[error.path] = error.message;
      });
      setErrors(newErrors);
      return;
    }

    const params = new FormData();
    params.append("ClientId", String(clientId ?? "").trim());
    params.append("SourceId", String(sourceId ?? "").trim());
    selectedFiles.forEach((file) => {
      params.append("Files", file, file.name);
    });

    setLoading(true);
    const response = await uploadCandidateBulkCv(params);
    setLoading(false);

    if (response?.success) {
      toast.success(response?.message);
      resetUploadForm();
    } else {
      toast.error(response?.message);
    }
  };

  const selectedFilesLabel = formatSelectedFilesLabel(selectedFiles);

  return (
    <div className="page-content upload-bulk-cv-page">
      <Container fluid>
        <div className="upload-bulk-cv-header mb-4">
          <div className="upload-bulk-cv-header-icon">
            <i className="mdi mdi-file-upload-outline" aria-hidden />
          </div>
          <div>
            <h4 className="fw-bold mb-1">Upload Bulk CV</h4>
            <p className="text-muted mb-0">
              Select a client and source, then upload PDF or image CV files.
            </p>
          </div>
        </div>

        <Card className="upload-bulk-cv-panel border-0 shadow-sm">
          <CardBody className="p-4">
            <Form onSubmit={handleUpload}>
              <Row className="g-3 upload-bulk-cv-form-row">
                <Col xs="12" lg="3">
                  <div className="upload-bulk-cv-field">
                    <Label className="upload-bulk-cv-label fw-semibold">
                      Client <span className="text-danger">*</span>
                    </Label>
                    <div
                      className={`upload-bulk-cv-control${
                        errors.clientId ? " is-invalid" : ""
                      }`}
                    >
                      <AsyncClientSelect
                        key={`upload-bulk-cv-client-${clientId}`}
                        pageName="CandidateFormRestrict"
                        value={clientId}
                        initialOption={buildClientAsyncFilterInitialOption(
                          clientId,
                          clientIdLabel
                        )}
                        onChange={(option) => {
                          setClientId(String(option?.value ?? "").trim());
                          setClientIdLabel(String(option?.label ?? "").trim());
                          if (errors.clientId) {
                            setErrors((prev) => ({ ...prev, clientId: "" }));
                          }
                        }}
                        placeholder="Select Client"
                      />
                    </div>
                    {errors.clientId ? (
                      <FormFeedback className="d-block mt-1">
                        {errors.clientId}
                      </FormFeedback>
                    ) : null}
                  </div>
                </Col>

                <Col xs="12" lg="3">
                  <div className="upload-bulk-cv-field">
                    <Label className="upload-bulk-cv-label fw-semibold">
                      Source <span className="text-danger">*</span>
                    </Label>
                    <div
                      className={`upload-bulk-cv-control${
                        errors.sourceId ? " is-invalid" : ""
                      }`}
                    >
                      <AsyncSourceSelect
                        key={`upload-bulk-cv-source-${sourceId}`}
                        value={sourceId}
                        onChange={(option) => {
                          setSourceId(String(option?.value ?? "").trim());
                          if (errors.sourceId) {
                            setErrors((prev) => ({ ...prev, sourceId: "" }));
                          }
                        }}
                        placeholder="Select Source"
                      />
                    </div>
                    {errors.sourceId ? (
                      <FormFeedback className="d-block mt-1">
                        {errors.sourceId}
                      </FormFeedback>
                    ) : null}
                  </div>
                </Col>

                <Col xs="12" lg="4">
                  <div className="upload-bulk-cv-field">
                    <Label className="upload-bulk-cv-label fw-semibold">
                      CV File <span className="text-danger">*</span>
                    </Label>
                    <div
                      className={`upload-bulk-cv-control${
                        errors.files ? " is-invalid" : ""
                      }`}
                    >
                      <button
                        type="button"
                        className={`upload-bulk-cv-dropzone w-100${
                          errors.files ? " upload-bulk-cv-dropzone--invalid" : ""
                        }${selectedFiles.length ? " upload-bulk-cv-dropzone--has-file" : ""}`}
                        onClick={openFilePicker}
                        disabled={loading}
                      >
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".pdf,application/pdf,image/*"
                          multiple
                          className="d-none"
                          onChange={handleFileChange}
                        />
                        <span className="upload-bulk-cv-dropzone-icon" aria-hidden>
                          <i className="mdi mdi-file-document-outline" />
                        </span>
                        <span className="upload-bulk-cv-dropzone-text text-truncate">
                          {selectedFilesLabel ? (
                            <span className="upload-bulk-cv-dropzone-filename">
                              {selectedFilesLabel}
                            </span>
                          ) : (
                            <span className="upload-bulk-cv-dropzone-placeholder">
                              <span className="fw-semibold">Choose PDF or image files</span>
                              <span className="text-muted"> · .pdf, images</span>
                            </span>
                          )}
                        </span>
                      </button>
                    </div>
                    {errors.files ? (
                      <FormFeedback className="d-block mt-1">{errors.files}</FormFeedback>
                    ) : null}
                  </div>
                </Col>

                <Col xs="12" lg="2">
                  <div className="upload-bulk-cv-field">
                    <Label
                      className="upload-bulk-cv-label upload-bulk-cv-label--spacer fw-semibold"
                      aria-hidden="true"
                    >
                      &nbsp;
                    </Label>
                    <div className="upload-bulk-cv-control">
                      <Button
                        color="primary"
                        type="submit"
                        className="upload-bulk-cv-submit-btn w-100"
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            Uploading…
                          </>
                        ) : (
                          <>
                            <i className="mdi mdi-cloud-upload-outline me-2" aria-hidden />
                            Upload
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </Col>
              </Row>
            </Form>
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default UploadBulkCV;
